// Independent dashboard validation for the protected order-date contract.
export type OrderDateRange = { createdFrom: string; createdTo: string; startMs: number; endMs: number };
export type OrderDateKey = { seconds: number; nanoseconds: number };
const dayMs = 86_400_000;
const stockholm = new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Stockholm", year: "numeric", month: "2-digit",
  day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" });
function calendarDay(value: unknown): number {
  if (typeof value !== "string" || !/^20\d{2}-\d{2}-\d{2}$/.test(value)) throw new Error("Invalid order date");
  const ms = Date.parse(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(ms) || new Date(ms).toISOString().slice(0, 10) !== value) throw new Error("Invalid order date");
  return ms;
}
function midnight(calendarMs: number): number {
  let candidate = calendarMs;
  for (let pass = 0; pass < 3; pass++) {
    const parts = Object.fromEntries(stockholm.formatToParts(candidate).map((part) => [part.type, part.value]));
    const wallMs = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
    if (wallMs === calendarMs) return candidate;
    candidate += calendarMs - wallMs;
  }
  throw new Error("Invalid order date");
}
export function orderDateRange(createdFrom?: string, createdTo?: string): OrderDateRange | null {
  if (createdFrom === undefined && createdTo === undefined) return null;
  const from = calendarDay(createdFrom), to = calendarDay(createdTo);
  if (to < from || (to - from) / dayMs >= 366) throw new Error("Invalid order date range");
  return { createdFrom: createdFrom!, createdTo: createdTo!, startMs: midnight(from), endMs: midnight(to + dayMs) };
}
export function compareOrderDateKeys(a: OrderDateKey, aId: string, b: OrderDateKey, bId: string): number {
  return a.seconds - b.seconds || a.nanoseconds - b.nanoseconds || (aId < bId ? -1 : aId > bId ? 1 : 0);
}
export function validOrderDateKey(value: unknown): value is OrderDateKey {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  return Object.keys(row).length === 2 && Number.isSafeInteger(row.seconds) && Number(row.seconds) >= 946_677_600 && Number(row.seconds) <= 4_102_444_800 &&
    Number.isSafeInteger(row.nanoseconds) && Number(row.nanoseconds) >= 0 && Number(row.nanoseconds) <= 999_999_999;
}
type CursorFilters = { paymentState: string; mode: string; reservationState?: string; fulfilmentState?: string; createdFrom?: string; createdTo?: string };
export function readOrderDateCursor(cursor: string, filters: CursorFilters): { key: OrderDateKey; lastId: string } | null {
  try {
    if (!/^[A-Za-z0-9_-]{1,512}$/.test(cursor)) return null;
    const raw = JSON.parse(atob(cursor.replace(/-/g, "+").replace(/_/g, "/")));
    const range = orderDateRange(filters.createdFrom, filters.createdTo);
    if (!range || !raw || raw.v !== 3 || !validOrderDateKey(raw.t) || typeof raw.i !== "string" || !/^[A-Za-z0-9_-]{1,128}$/.test(raw.i)) return null;
    const ms = raw.t.seconds * 1000 + Math.floor(raw.t.nanoseconds / 1_000_000);
    const canonical = { v: 3, p: filters.paymentState, m: filters.mode, r: filters.reservationState ?? "all", f: filters.fulfilmentState ?? "all",
      a: filters.createdFrom, b: filters.createdTo, t: { seconds: raw.t.seconds, nanoseconds: raw.t.nanoseconds }, i: raw.i };
    const encoded = btoa(JSON.stringify(canonical)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    return ms >= range.startMs && ms < range.endMs && encoded === cursor ? { key: raw.t, lastId: raw.i } : null;
  } catch { return null; }
}
